/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  BavelPayTravelClient,
  BavelPayTravelUpdateRequest,
  FullTrack,
  IBavelPayTravelUpdateRequest,
  IPaymentDetailResponse
} from '@app/services/bavel-payment-manager-client.service';
import { Component, Input, EventEmitter, Output, OnInit, inject } from '@angular/core';
import { PaymentStatusEnum } from '@app/bavel-collect-travel/enums/payment-status.enum';
import * as _ from 'lodash';
import { IUsersService } from '@shared/interfaces/users-service.interface';
import { catchError, EMPTY, finalize, Observable, tap } from 'rxjs';

@Component({
  selector: 'app-payment-detail',
  templateUrl: './payment-detail.component.html',
  styleUrls: ['./payment-detail.component.scss'],
  standalone: false
})
export class PaymentDetailComponent implements OnInit {
  @Input() public editMode = false;
  @Input() public paymentDetail: IPaymentDetailResponse = {} as IPaymentDetailResponse;
  @Output() public hide: EventEmitter<void> = new EventEmitter();
  @Output() public saveChanges: EventEmitter<BavelPayTravelUpdateRequest> = new EventEmitter();
  public visible = false;
  public pciAttemptsMessage = '';
  public pciIframeUrl: any = null;
  public showPciBookingIframe = false;
  public showSpinner: boolean = false;

  private bavelPayTravelClient: BavelPayTravelClient = inject(BavelPayTravelClient);
  private paymentDetailOriginal: IPaymentDetailResponse = {} as IPaymentDetailResponse;
  private usersService: IUsersService = inject(IUsersService);

  public ngOnInit(): void {
    this.paymentDetailOriginal = _.cloneDeep(this.paymentDetail);
    this.getFullTrack().subscribe({
      complete: () => this.visible = true
    });
  }

  public get canDeobfuscateFullTrack(): boolean {
    const isActiveOrSettledPayment = this.paymentDetail.status === PaymentStatusEnum.ACTIVE.valueOf() ||
      this.paymentDetail.status === PaymentStatusEnum.SETTLED.valueOf();
    const userIsNotImpersonated = !this.usersService.isImpersonated;

    return isActiveOrSettledPayment && userIsNotImpersonated;
  }

  private getFullTrack(): Observable<FullTrack> {
    if (this.canDeobfuscateFullTrack) {
      this.showSpinner = true;
      return this.bavelPayTravelClient.getFullTrack(this.paymentDetail.piid).pipe(
        tap((fullTrack: FullTrack) => {
          if (this.paymentDetail.card) {
            this.paymentDetail.card.cardHolder = fullTrack.cardHolder;
            this.paymentDetail.card.cvv = fullTrack.cvv;
            this.paymentDetail.card.expiryDate = fullTrack.expiryDate;
            this.paymentDetail.card.pan = fullTrack.pan;
          }
        }),
        catchError(() => {
          this.showSpinner = false;
          return EMPTY;
        }),
        finalize(() => {
          this.showSpinner = false;
        })
      );
    }
    return EMPTY;
  }

  public onHide(): void {
    this.hide.emit();
  }

  public save(): void {
    this.adjustTimeZoneOffset();
    const paymentDetail: IBavelPayTravelUpdateRequest = {
      amount: this.compareValues(this.paymentDetail.amount, this.paymentDetailOriginal.amount),
      bookings: this.compareValues(this.paymentDetail.bookings, this.paymentDetailOriginal.bookings),
      comments: this.compareValues(this.paymentDetail.comments, this.paymentDetailOriginal.comments),
      effectiveMaxDate: this.paymentDetail.effectiveMaxDate,
      effectiveMinDate: this.paymentDetail.effectiveMinDate,
      piid: this.paymentDetail.piid
    };

    this.saveChanges.emit(paymentDetail as BavelPayTravelUpdateRequest);
  }

  private adjustTimeZoneOffset(): void {
    this.paymentDetail.effectiveMaxDate?.setMinutes(Math.abs(this.paymentDetail.effectiveMaxDate.getTimezoneOffset()));
    this.paymentDetail.effectiveMinDate?.setMinutes(Math.abs(this.paymentDetail.effectiveMinDate.getTimezoneOffset()));

    this.paymentDetailOriginal.effectiveMaxDate?.setMinutes(Math.abs(this.paymentDetailOriginal.effectiveMaxDate.getTimezoneOffset()));
    this.paymentDetailOriginal.effectiveMinDate?.setMinutes(Math.abs(this.paymentDetailOriginal.effectiveMinDate.getTimezoneOffset()));

    if (this.paymentDetail.bookings) {
      this.paymentDetail.bookings.forEach(booking => {
        booking.checkinDate?.setMinutes(Math.abs(booking.checkinDate.getTimezoneOffset()));
        booking.checkoutDate?.setMinutes(Math.abs(booking.checkoutDate.getTimezoneOffset()));
      });
    }

    if (this.paymentDetailOriginal.bookings) {
      this.paymentDetailOriginal.bookings.forEach(booking => {
        booking.checkinDate?.setMinutes(Math.abs(booking.checkinDate.getTimezoneOffset()));
        booking.checkoutDate?.setMinutes(Math.abs(booking.checkoutDate.getTimezoneOffset()));
      });
    }
  }

  private compareValues(editedValue: any, originalValue: any): any {
    return _.isEqual(editedValue, originalValue) ? null : editedValue;
  }
}
